import React, { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { delegationService as defaultDelegationService, isDelegationApiError } from '../services/delegationService';
import type { DelegationService } from '../services/delegationService';
import type { ApprovalDelegation, DelegationApiError } from '../types/delegation.types';
import { DelegationValidationError } from './DelegationValidationError';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RevokeDelegationDialogProps {
  delegation: ApprovalDelegation | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: (revoked: ApprovalDelegation) => void;
  delegationService?: DelegationService;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RevokeDelegationDialog: React.FC<RevokeDelegationDialogProps> = ({
  delegation,
  onOpenChange,
  onSuccess,
  delegationService = defaultDelegationService,
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DelegationApiError | null>(null);

  const open = delegation !== null;

  function handleOpenChange(next: boolean) {
    if (!loading) {
      if (!next) {
        setReason('');
        setError(null);
      }
      onOpenChange(next);
    }
  }

  async function handleConfirm() {
    if (!delegation) return;

    try {
      setLoading(true);
      setError(null);
      const response = await delegationService.revokeDelegation(delegation.id, { reason: reason.trim() || undefined });
      onSuccess(response.delegation);
      setReason('');
    } catch (err) {
      if (isDelegationApiError(err)) {
        setError(err);
      } else {
        setError({ error: 'forbidden', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  }

  if (!delegation) return null;

  const dateRange = `${format(parseISO(delegation.startDate), 'd MMM yyyy')} – ${format(parseISO(delegation.endDate), 'd MMM yyyy')}`;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            <AlertDialogTitle>Revoke Delegation</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            You are about to revoke the delegation to{' '}
            <span className="font-medium text-foreground">{delegation.delegateName}</span>{' '}
            ({dateRange}).
          </AlertDialogDescription>
          <AlertDialogDescription className="mt-1">
            {delegation.delegateName} will immediately lose the ability to approve timesheets on your behalf. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-1">
          <Label htmlFor="revoke-reason" className="text-sm">
            Reason <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id="revoke-reason"
            placeholder="e.g. Returned early from leave"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            disabled={loading}
            className="resize-none"
          />
        </div>

        <DelegationValidationError apiError={error} />

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {loading ? 'Revoking…' : 'Revoke Delegation'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
